import React, { useEffect, useMemo, useRef, useState } from 'react';
import TextTruncate from 'react-text-truncate';
import { Link } from 'components/Link/Link';
import { type App, Permission, type Product } from 'generated/graphql';

import { getMaxMinPrice } from 'utils/ListItem';
import { useAvailabilityTypeByVariablesByApp } from 'utils';

import AvailabilityPill from 'components/AvailabilityPill/AvailabilityPill';
import IconQuestion from 'icons/ic_ErrorQuestion_black.svg';
import ProductImage from 'components/Image/ProductImage';
import { useVenueContext } from 'routes/Main';
import useScreenWidthQuery from 'common/Hooks/UseScreenWidthQuery';
import { availabilities } from 'utils/constants';
import styles from './CategoryListItem.module.scss';
import { SelectMenu } from '../SelectMenu';
import { useModalContext } from '../../common/Context/ModalContext';
import { useCategoryContext } from './CategoryContext';
import { WindowBreakpoints } from '../../utils/DocumentHelper';

interface Props {
  menuItem: Product;
  isOpen: boolean;
  isLoading?: boolean;
  apps: App[];
  categoryId: string;
  modified?: boolean;
}

const CategoryListItem: React.FC<Props> = ({ menuItem, isOpen, isLoading, apps, categoryId, modified = false }) => {
  const app = apps[0];

  const { name, description, variablesByApp } = menuItem;

  // Get image from variablesByApp matching app ID. If not found, get image from first variablesByApp with imageUri
  const imageUri =
    variablesByApp.find((vba) => vba.app.id === app.id)?.imageUri ||
    variablesByApp.find((vba) => vba.imageUri)?.imageUri;

  const availability = useAvailabilityTypeByVariablesByApp(variablesByApp);
  const availabilityName = availabilities.find((a) => a.slug === availability)?.name || '';

  const surcharge = useMemo(() => {
    return variablesByApp.find((variable) => variable.app.id === app.id)?.surcharge;
  }, [variablesByApp, app.id]);

  const [minPrice, maxPrice] = getMaxMinPrice(menuItem, apps);
  const { openModal, closeModal } = useModalContext();
  const { deleteProduct } = useCategoryContext();
  const [isHover, setIsHover] = useState(false);

  const { currentUser } = useVenueContext();
  const canEditItem = !!currentUser?.permissions?.includes(Permission.PublishMenu);

  const [innerWidth] = useScreenWidthQuery();

  // TextTruncate updates whenever the component updates, but this causes some layout thrashing
  const truncateRef = useRef<TextTruncate>(null);
  const truncateRefOuter = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      if (truncateRef.current) {
        truncateRef.current.forceUpdate();
      }
    }, 50);

    setTimeout(() => {
      if (truncateRefOuter.current && !truncateRefOuter.current.style.height) {
        const { height } = truncateRefOuter.current.getBoundingClientRect();

        truncateRefOuter.current.style.height = `${height}px`;
      }
    }, 100);
  }, [isOpen]);

  return (
    <div data-testid={`category-list-item-${menuItem.posId}`}>
      <div
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        className={`${styles.Item} ${availability === 'unavailable' && styles.Unavailable} ${
          !canEditItem && styles.ItemNoGrabbing
        }`}
      >
        <Link href={`items/${menuItem.mmId}`} aria-label={`${menuItem.name}. Press Enter to open side panel`}>
          <div className={styles.ItemLink} />
        </Link>
        <div className={styles.ItemDetails}>
          <ProductImage
            isLoading={isLoading}
            testId="category-list-item-image"
            alt={name}
            src={imageUri || '/Images/NoImage.svg'}
          />
          <div>
            <div className={styles.StretchyTitleContainer}>
              <div className={styles.StretchyTitle}>
                <h4 className={isLoading ? 'LoadingCoverText' : ''}>{name}</h4>
              </div>
              {modified ? (
                <div className={styles.ModifiedIcon}>
                  <i className={`Dot mixed ${styles.AbsoluteDot}`} />
                </div>
              ) : null}
            </div>
            <span className={`${styles.Price} ${isLoading ? 'LoadingCoverText' : ''}`}>
              {surcharge && surcharge.amount > 0 && (
                <div className={styles.Surcharge}>
                  <span>%</span>
                </div>
              )}
              {minPrice === maxPrice ? (
                minPrice
              ) : (
                <>
                  {minPrice} - {maxPrice} {!isLoading && <img src={IconQuestion} alt="Question mark icon" />}
                </>
              )}
            </span>
            {description && availability === 'available' ? (
              <div className={styles.DescriptionCont} ref={truncateRefOuter} data-testid="list-item-text-outer">
                {isLoading ? (
                  <div className={styles.Description}>
                    <span className="LoadingCoverText">XXX XXXXX XXXXX</span>
                  </div>
                ) : (
                  <TextTruncate
                    line={2}
                    element="span"
                    truncateText="..."
                    text={description}
                    containerClassName={styles.Description}
                    ref={truncateRef}
                  />
                )}
              </div>
            ) : null}
            {availability !== 'available' && !isLoading ? (
              <div>
                <AvailabilityPill editable={false} availability={availabilityName} />
              </div>
            ) : null}
          </div>
          {canEditItem && (isHover || innerWidth < WindowBreakpoints.MD) ? (
            <div className={styles.SelectMenuSection}>
              <SelectMenu
                aria-label="Item Details"
                options={[
                  {
                    content: (
                      <span className={styles.Red}>
                        <i className={`mm-trash-o ${styles.Icon}`} /> Delete item
                      </span>
                    ),
                    isRedHoverBackground: true,
                    value: 'delete-item',
                    type: 'button',
                    callback: () => {
                      openModal({
                        type: 'ERROR',
                        title: `Delete this item?`,
                        body: (
                          <span>
                            This item will be deleted from this menu category. <br /> Your POS data will not be
                            affected.
                          </span>
                        ),
                        buttons: [
                          {
                            label: 'Cancel',
                            callback: closeModal,
                            level: 'low'
                          },
                          {
                            label: 'Confirm',
                            callback: () => deleteProduct(categoryId, menuItem),
                            level: 'high'
                          }
                        ],
                        canDismiss: true
                      });
                    }
                  }
                ]}
                defaultButtonContent={<i className="mm-ellipsis-v" />}
                buttonStyle={styles.ItemSelectMenuButton}
                downIcon={null}
                lineBeforeLast={false}
                align="right"
                size="small"
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CategoryListItem;
